import { ArrowRight, Play, Sparkles } from 'lucide-react'
import { Link } from 'react-router'

import { useTodayDaylist } from '@/api/hooks'
import { Button, Skeleton, Spinner } from '@/components/ui'
import { ErrorNotice } from '@/components/ui/Feedback'
import { PlaylistMosaic } from '@/features/playlists/PlaylistMosaic'
import { playDaylist, useDaylistPreparation } from './daylistPlayback'

export function DaylistCard() {
  const query = useTodayDaylist()
  const preparing = useDaylistPreparation((state) => state.daylistId)
  if (query.isPending) return <Skeleton className="h-44 rounded-card" />
  if (query.isError) return (
    <ErrorNotice busy={query.isFetching} onRetry={() => void query.refetch()}>Your daylist couldn’t be loaded.</ErrorNotice>
  )
  const daylist = query.data
  if (!daylist) return null
  if (daylist.status !== 'ready') return (
    <section aria-label="Your daily mix" className="flex items-center gap-3 rounded-card border border-border bg-surface-raised p-4">
      {['queued', 'generating'].includes(daylist.status) ? <Spinner size="sm" label="Creating daylist" /> : <Sparkles className="h-5 w-5 shrink-0 text-accent" aria-hidden="true" />}
      <div>
        <h2 className="text-sm font-semibold text-text">Your daily mix</h2>
        <p className="mt-1 text-sm text-text-muted">{['queued', 'generating'].includes(daylist.status) ? 'Putting familiar favourites and new discoveries together…' : daylist.message}</p>
      </div>
    </section>
  )
  const familiar = daylist.entries.filter((entry) => entry.familiar).length
  return (
    <section aria-labelledby="daylist-heading" className="overflow-hidden rounded-card border border-accent/25 bg-gradient-to-br from-accent-soft to-surface-raised p-4 sm:p-6">
      <div className="flex items-center gap-4 sm:gap-6">
        <Link to={`/daylists/${daylist.id}`} aria-label={`Open ${daylist.name}`} className="w-20 shrink-0 sm:w-32">
          <PlaylistMosaic coverUrls={daylist.entries.map((entry) => entry.track.artwork_url).filter(Boolean).slice(0, 4)} className="w-full shadow-lg" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-accent"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />Made for today</p>
          <h2 id="daylist-heading" className="text-xl font-semibold tracking-tight text-text sm:text-2xl"><Link to={`/daylists/${daylist.id}`}>{daylist.name}</Link></h2>
          <p className="mt-2 text-sm text-text-muted">{familiar} familiar · {daylist.entries.length - familiar} discoveries</p>
          {daylist.seed_artists.length > 0 ? <p className="mt-1 truncate text-xs text-text-muted">Inspired by {daylist.seed_artists.slice(0, 3).join(', ')}</p> : null}
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between gap-3">
        <Button onClick={() => void playDaylist(daylist)} loading={preparing === daylist.id}><Play className="h-4 w-4 fill-current" aria-hidden="true" />Play daylist</Button>
        <Link to={`/daylists/${daylist.id}`} className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-text-muted hover:text-text">View mix <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
      </div>
    </section>
  )
}
